import "./FloatingPanel.scss";
import cn from "classnames";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { BsLayoutSidebar } from "react-icons/bs";
import { MdFullscreenExit, MdOpenInFull, MdRemove } from "react-icons/md";
import { Mixpanel, MxE } from "../../utils/mixpanelWrapper";
import { Button, ButtonProps, ButtonType } from "../Button/Button";
import { ButtonFrameworkVariant } from "../ButtonFramework/ButtonFramework";
import SidePanel from "./SidePanel";

const EXPAND_BUFFER_SPACE = 32;

export const FloatingPanelActionButton = (passthroughProps: Partial<ButtonProps>): JSX.Element => (
  <Button
    type={ButtonType.EMPTY}
    variant={ButtonFrameworkVariant.ICON_ONLY}
    size="sm"
    design="secondary"
    tooltipClass="floatingPanelTooltip"
    {...passthroughProps}
  />
);

// Because browser origin (0,0) is positioned at top-left,
// negative-x = left, negative-y = up, positive-x = right, positive-y = down
type DirectionType = "negative" | "positive";
interface ResizeDirection {
  x: DirectionType | undefined;
  y: DirectionType | undefined;
}
interface ResizeHandle {
  name: string;
  direction: ResizeDirection;
}
type DragActionType = "resize" | "reposition";
interface DragAction {
  type: DragActionType;
  direction?: ResizeDirection;
}

const resizeHandleDirections: ResizeHandle[] = [
  { name: "topLeftHandle", direction: { x: "negative", y: "negative" } },
  { name: "topHandle", direction: { x: undefined, y: "negative" } },
  { name: "topRightHandle", direction: { x: "positive", y: "negative" } },
  { name: "rightHandle", direction: { x: "positive", y: undefined } },
  { name: "bottomRightHandle", direction: { x: "positive", y: "positive" } },
  { name: "bottomHandle", direction: { x: undefined, y: "positive" } },
  { name: "bottomLeftHandle", direction: { x: "negative", y: "positive" } },
  { name: "leftHandle", direction: { x: "negative", y: undefined } },
];

interface FloatingPanelProps {
  id: string;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode;
  sidePanelTitle?: React.ReactNode;
  sidePanelRightActions?: React.ReactNode;
  sidePanelBody?: React.ReactNode;
  sidePanelFooter?: React.ReactNode;
  show: boolean;
  onHide: () => void;
  showSidePanel: boolean;
  setShowSidePanel: Dispatch<SetStateAction<boolean>>;
  minWidth?: number;
  maxWidth?: number;
  initialWidth: number;
  minHeight?: number;
  maxHeight?: number;
  initialHeight: number;
  initialPosition: {
    xDist: number;
    xDirection: "left" | "right";
    yDist: number;
    yDirection: "top" | "bottom";
  };
}

const FloatingPanel = ({
  id,
  leftActions,
  rightActions,
  body,
  footer,
  sidePanelTitle,
  sidePanelRightActions,
  sidePanelBody,
  sidePanelFooter,
  show,
  showSidePanel,
  setShowSidePanel,
  onHide,
  minWidth = -Infinity,
  maxWidth: maxWidthProp = Infinity,
  initialWidth,
  minHeight = -Infinity,
  maxHeight: maxHeightProp = Infinity,
  initialHeight,
  initialPosition,
}: FloatingPanelProps): JSX.Element => {
  const [innerWidth, setInnerWidth] = useState<number>(window.innerWidth);
  const [innerHeight, setInnerHeight] = useState<number>(window.innerHeight);
  const [width, setWidth] = useState<number>(initialWidth);
  const [height, setHeight] = useState<number>(initialHeight);
  // (0, 0) is the top-left corner of the workable area of the page, discounting navbar + header
  const [x, setX] = useState<number>(0);
  const [y, setY] = useState<number>(0);
  const [dragAction, setDragAction] = useState<DragAction | undefined>();

  useEffect(() => {
    const handleResize = () => {
      setInnerWidth(window.innerWidth);
      setInnerHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [navbarWidth, headerHeight] = useMemo(() => {
    const navbarEl = document?.querySelector(".bpm-navbar") as HTMLElement | null;
    const headerEl = document?.querySelector(".bpmPage > .header") as HTMLElement | null;
    const navbarWidth = navbarEl?.offsetWidth ?? 0;
    const headerHeight = headerEl?.offsetHeight ?? 0;
    return [navbarWidth, headerHeight];
  }, []);

  const [rightEdgeX, bottomEdgeY] = useMemo(
    () => [innerWidth - navbarWidth, innerHeight - headerHeight],
    [headerHeight, innerHeight, innerWidth, navbarWidth]
  );

  const [maxX, maxY] = useMemo(() => [rightEdgeX - minWidth, bottomEdgeY - minHeight], [
    bottomEdgeY,
    minHeight,
    minWidth,
    rightEdgeX,
  ]);

  const [initialX, initialY] = useMemo(() => {
    const { xDist, xDirection, yDist, yDirection } = initialPosition;

    const computedX =
      xDirection === "left" ? xDist : innerWidth - navbarWidth - xDist - initialWidth;
    const computedY =
      yDirection === "top" ? yDist : innerHeight - headerHeight - yDist - initialHeight;
    const normalizedX = computedX < 0 || computedX > maxX ? 0 : computedX;
    const normalizedY = computedY < 0 || computedY > maxY ? 0 : computedY;

    return [normalizedX, normalizedY];
  }, [
    headerHeight,
    initialHeight,
    initialPosition,
    initialWidth,
    innerHeight,
    innerWidth,
    maxX,
    maxY,
    navbarWidth,
  ]);

  useEffect(() => {
    if (!show) {
      setX(initialX);
      setY(initialY);
    }
  }, [initialX, initialY, show]);

  const [maxWidth, maxHeight] = useMemo(() => {
    const screenMaxWidth = innerWidth - navbarWidth;
    const computedMaxWidth = Math.min(screenMaxWidth, maxWidthProp);

    const screenMaxHeight = innerHeight - headerHeight;
    const computedMaxHeight = Math.min(screenMaxHeight, maxHeightProp);
    return [computedMaxWidth, computedMaxHeight];
  }, [headerHeight, innerHeight, innerWidth, maxHeightProp, maxWidthProp, navbarWidth]);

  const resetState = useCallback(() => {
    setShowSidePanel(false);
    setWidth(initialWidth);
    setHeight(initialHeight);
    setX(initialX);
    setY(initialY);
    setDragAction(undefined);
  }, [initialHeight, initialWidth, initialX, initialY, setShowSidePanel]);

  // When floating panel is hidden, reset sizing + positioning to initial
  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [resetState, show]);

  const isModified = useMemo(
    () => width !== initialWidth || height !== initialHeight || x !== initialX || y !== initialY,
    [height, width, x, y, initialHeight, initialWidth, initialX, initialY]
  );

  const handleDrag: React.MouseEventHandler<HTMLDivElement> = e => {
    if (dragAction) {
      const { movementX, movementY } = e;
      const { type, direction } = dragAction;
      const { x: xDirection, y: yDirection } = direction ?? {};

      if (type === "reposition") {
        const newX = x + movementX;
        const newY = y + movementY;

        // Shink if new x position would push off the right edge of screen
        if (newX + width > rightEdgeX && rightEdgeX - newX > minWidth) {
          setWidth(rightEdgeX - newX);
        }

        // Shink if new y position would push off the bottom edge of screen
        if (newY + height > bottomEdgeY && bottomEdgeY - newY > minHeight) {
          setHeight(bottomEdgeY - newY);
        }

        // Move x/y to new position as long as they don't exit the working area
        if (newX >= 0 && newX <= maxX) {
          setX(current => current + movementX);
        }

        if (newY >= 0 && newY <= maxY) {
          setY(current => current + movementY);
        }
      } else if (type === "resize") {
        if (xDirection === "negative") {
          // movements from left handles
          if (width - movementX >= minWidth) {
            setX(current => current + movementX);
            setWidth(current => current - movementX);
          }
        } else if (xDirection === "positive") {
          // movements from right handles
          if (width + movementX >= minWidth) {
            setWidth(current => current + movementX);
          }
        }

        if (yDirection === "negative") {
          // movements from top handles
          if (height - movementY >= minHeight) {
            setY(current => current + movementY);
            setHeight(current => current - movementY);
          }
        } else if (yDirection === "positive") {
          // movements from bottom handles
          if (height + movementY >= minHeight) {
            setHeight(current => current + movementY);
          }
        }
      }
    }
  };

  const endDrag: React.MouseEventHandler<HTMLDivElement> = useCallback(() => {
    setDragAction(undefined);
  }, []);

  const openSidePanel = () => {
    setShowSidePanel(true);
    Mixpanel.track(MxE.FLOATING_PANEL_OPEN_SIDEPANEL, {
      widget: id,
    });
  };

  const closeSidePanel = useCallback(() => {
    setShowSidePanel(false);
    Mixpanel.track(MxE.FLOATING_PANEL_CLOSE_SIDEPANEL, {
      widget: id,
    });
  }, [id, setShowSidePanel]);

  const mainPanelOnClick = useMemo(() => (showSidePanel ? closeSidePanel : undefined), [
    closeSidePanel,
    showSidePanel,
  ]);

  const minimize = () => {
    onHide();
    Mixpanel.track(MxE.FLOATING_PANEL_MINIMIZE, {
      widget: id,
    });
  };

  const resetSizePosition = () => {
    resetState();
    Mixpanel.track(MxE.FLOATING_PANEL_RESET, {
      widget: id,
    });
  };

  const expand = () => {
    setWidth(maxWidth - EXPAND_BUFFER_SPACE * 2);
    setHeight(maxHeight - EXPAND_BUFFER_SPACE * 2);

    const extraXSpace = innerWidth - navbarWidth - maxWidth;
    const extraYSpace = innerHeight - headerHeight - maxHeight;

    // Position FloatingPanel to be centered at it's maximum size
    setX(extraXSpace / 2 + EXPAND_BUFFER_SPACE);
    setY(extraYSpace / 2 + EXPAND_BUFFER_SPACE);

    Mixpanel.track(MxE.FLOATING_PANEL_EXPAND, {
      widget: id,
    });
  };

  return (
    <div
      className={cn("floatingPanelContainer", { dragActive: dragAction !== undefined })}
      style={{
        width: `calc(100vw - ${navbarWidth}px)`,
        left: `${navbarWidth}px`,
        height: `calc(100vh - ${headerHeight}px)`,
        top: `${headerHeight}px`,
      }}
      onMouseMove={e => handleDrag(e)}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
    >
      <div
        id={id}
        className={cn("floatingPanel", {
          open: show,
          closed: !show,
        })}
        style={{ width: width, height: height, translate: `${x}px ${y}px` }}
      >
        <SidePanel
          title={sidePanelTitle}
          rightActions={sidePanelRightActions}
          body={sidePanelBody}
          footer={sidePanelFooter}
          show={showSidePanel}
          onHide={closeSidePanel}
        />
        <div className="mainPanel" onClick={mainPanelOnClick}>
          <div
            className="floatingPanelHeader"
            onMouseDown={() => {
              setDragAction({ type: "reposition" });
              Mixpanel.track(MxE.FLOATING_PANEL_DRAG_REPOSITION, {
                widget: id,
              });
            }}
          >
            <div className="floatingPanelLeftActions">
              <div className="floatingPanelAction">
                <FloatingPanelActionButton
                  // TODO: Spec icon in react-icons/fi requires upgrade to react-icons 5+
                  // icon={<FiSidebar />}
                  icon={<BsLayoutSidebar />}
                  onClick={openSidePanel}
                  tooltipText="Open side panel"
                  exitTooltipOnClick
                />
              </div>
              {leftActions}
            </div>
            <div className="floatingPanelRightActions">
              {rightActions}
              <div className="floatingPanelAction">
                <FloatingPanelActionButton
                  icon={<MdOpenInFull />}
                  onClick={expand}
                  tooltipText="Expand"
                />
              </div>
              {isModified && (
                <div className="floatingPanelAction">
                  <FloatingPanelActionButton
                    icon={<MdFullscreenExit />}
                    onClick={resetSizePosition}
                    tooltipText="Reset"
                  />
                </div>
              )}
              <div className="floatingPanelAction">
                <FloatingPanelActionButton
                  icon={<MdRemove />}
                  onClick={minimize}
                  tooltipText="Minimize"
                />
              </div>
            </div>
          </div>
          <div className="floatingPanelBody">{body}</div>
          {footer && <div className="floatingPanelFooter">{footer}</div>}
        </div>
        <div className="handlesContainer">
          {resizeHandleDirections.map(({ name, direction }) => (
            <div
              key={name}
              className={`handle ${name}`}
              onMouseDown={() => {
                setDragAction({
                  type: "resize",
                  direction: direction,
                });
                Mixpanel.track(MxE.FLOATING_PANEL_DRAG_RESIZE, {
                  widget: id,
                });
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FloatingPanel;
